import { computed, onBeforeUnmount, reactive, ref, watch, type Ref } from "vue";
import {
  hasRenditions,
  renditionUrl,
  videoFrameUrl,
  videoPosterUrl,
} from "~/utils/cdn";
import {
  buildProductGalleryImageAlt,
  buildProductVideoAlt,
  mergeProductMediaItems,
} from "~/core/utils/productImageAlt";

// ---------------------------------------------------------------------------
// Video posters
// ---------------------------------------------------------------------------
// This module used to grab the poster by downloading the clip itself into a
// detached <video preload="auto">, seeking to 10% and painting a frame into a
// canvas — on EVERY product view, immediately (that watcher ran with
// `immediate: true`). On a phone that is tens of megabytes of cellular data and
// a video decode competing with hydration, all to produce one still image.
//
// The poster is now a real file on the CDN, derived from the clip's own URL by
// `videoPosterUrl()` (see the convention documented there). It is a normal
// AVIF with the usual sized renditions, so it costs ~20 KB and is fetched by
// the same warm-up path as every other gallery photo.
//
// When that AVIF cannot be shown (not generated for an imported clip, or a
// decoder that rejects it - iPhones showed the product photo instead of the
// poster on 2026-10-01), the `error` handlers of the gallery slide and the
// desktop thumbnail rail step to `posterFrame` (the JPEG still the module's
// MediaConvert job captured, same picture) and then to `posterFallback` (the
// product's own hero photo), both carried on every video item.

export function useProductMediaGallery(
  product: Ref<Record<string, any> | null>
) {
  // The PDP's `images` field (from the full product query) is a secondary
  // gallery list that does NOT include the product's main image — so using
  // it alone puts a different photo in slide 1 than the one shown as the
  // product's image everywhere else (catalog grid, search, SEO/share image).
  // Mirror the catalog thumb's ordering (new-product-thumb.vue catalogImages):
  // main image first, then the gallery images, deduped by URL. When a catalog
  // preview has already been committed, `images[0]` IS the main image, so the
  // dedupe below is a no-op and nothing changes for that path.
  const productImages = computed(() => {
    const p = product.value;
    if (!p) return [];

    const galleryImages = Array.isArray(p.images) ? p.images : [];
    const seenGalleryUrls = new Set<string>();
    const uniqueGalleryImages = galleryImages.filter((item: any) => {
      const url = String(
        item?.image || item?.src || item?.imageBig || ""
      ).trim();
      if (!url || seenGalleryUrls.has(url)) return false;
      seenGalleryUrls.add(url);
      return true;
    });
    const withAlt = (item: any, index: number) => ({
      ...item,
      alt: buildProductGalleryImageAlt(p, index + 1),
    });
    const mainImage = p.image;
    if (!mainImage) {
      return uniqueGalleryImages.map((item: any, index: number) =>
        withAlt(item, index)
      );
    }

    const mainItem = withAlt(
      {
        image: mainImage,
        imageLazy: p.imageLazy,
        imageBig: p.imageBig || mainImage,
        seoAlt: p.seoAlt,
      },
      0
    );

    // Main image always leads, matching the catalog thumb's ordering — and
    // drop any duplicate of it from the rest of the list instead of just
    // skipping the reorder when it happens to already be present (it can
    // appear anywhere in `images`, including the very end).
    const rest = uniqueGalleryImages.filter(
      (img: any) => (img?.image || img?.src) !== mainImage
    );

    return [
      mainItem,
      ...rest.map((item: any, index: number) => withAlt(item, index + 1)),
    ];
  });

  const backendVideoUrls = computed(() => {
    const videos = Array.isArray(product.value?.videos)
      ? product.value.videos
      : [];

    return videos
      .filter(
        (videoUrl: unknown) => typeof videoUrl === "string" && videoUrl.trim()
      )
      .map((videoUrl: string) => videoUrl.trim());
  });

  const videoPosterFallback = computed(() => {
    const firstImage = productImages.value[0];

    return (
      firstImage?.image ||
      firstImage?.src ||
      firstImage?.imageLazy ||
      firstImage?.imageBig ||
      ""
    );
  });

  const videoItems = computed(() => {
    const fallback = videoPosterFallback.value;
    const fallbackPreview = hasRenditions(fallback)
      ? renditionUrl(fallback, 600, { ratio: 1, format: "avif" }) || fallback
      : fallback;

    return backendVideoUrls.value.map((videoUrl: string) => {
      // `image` is what every consumer (gallery slide, thumbnail rail, warm-up)
      // treats as this item's picture, so the CDN poster goes there.
      // `posterFrame` and then `posterFallback` are what the `error` handlers
      // swap in when the poster cannot be shown.
      const poster = videoPosterUrl(videoUrl);

      return {
        type: "video",
        image: poster || fallback,
        imageLazy: fallbackPreview,
        posterFrame: poster ? videoFrameUrl(videoUrl) : "",
        posterFallback: fallback,
        videoUrl,
        alt: buildProductVideoAlt(product.value),
      };
    });
  });

  const mediaItems = computed(() => {
    const images = productImages.value;

    if (videoItems.value.length === 0) return images;
    if (images.length === 0) return videoItems.value;

    // Main image leads, then the video(s), then the remaining photos —
    // mirrors vuefront's gallery ordering so the promo clip sits right after
    // the hero shot.
    return mergeProductMediaItems(images, videoItems.value);
  });

  // Playback (pause on swipe, pause offscreen, play only on user request) lives
  // entirely inside mobile-photo-gallery.vue, which owns the <video> elements.
  // This composable used to keep a parallel videoRefs map here, but nothing ever
  // populated it — `setVideoRef` was exported and never called — so every
  // play/pause call through it was a no-op. Removed rather than re-wired: two
  // components driving the same media elements is how playback state drifts.

  // --- Desktop gallery: MobilePhotoGallery (drag/spring + progress bar +
  // counter + tap-zoom) with a clickable vertical thumbnail strip beside it. ---
  const desktopGalleryRef = ref<any>(null);
  const desktopGalleryIndex = ref(0);
  const thumbsTrackRef = ref<HTMLElement | null>(null);
  const thumbGrabbing = ref(false);
  const visibleThumbIndices = reactive(new Set<number>());
  // Key by media URL, never by position: late loads from an old product must
  // not become previews for a different product at the same slide index.
  const loadedThumbnailSources = reactive<Record<string, string>>({});
  const onThumbLoad = (event: Event, slide: any) => {
    const image = event.currentTarget as HTMLImageElement | null;
    const source = image?.currentSrc || image?.src || "";
    const key = slide?.videoUrl || slide?.image || slide?.src || "";
    if (!key || !image?.complete || image.naturalWidth <= 1 || !source) return;
    loadedThumbnailSources[key] = source;
  };
  let thumbnailObserver: IntersectionObserver | null = null;
  // Native lazy loading eagerly fetches most of a clipped thumbnail rail. Only
  // assign a source when the thumbnail actually reaches the scroll viewport.
  watch(
    [thumbsTrackRef, mediaItems],
    ([track]) => {
      thumbnailObserver?.disconnect();
      visibleThumbIndices.clear();
      if (!track) return;
      if (typeof IntersectionObserver === "undefined") {
        mediaItems.value.forEach((_, index) => visibleThumbIndices.add(index));
        return;
      }
      thumbnailObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            visibleThumbIndices.add(
              Number((entry.target as HTMLElement).dataset.mediaIndex)
            );
            thumbnailObserver?.unobserve(entry.target);
          }
        },
        { root: track, rootMargin: "0px", threshold: 0 }
      );
      for (const thumb of Array.from(track.children))
        thumbnailObserver.observe(thumb);
    },
    { flush: "post" }
  );
  let thumbDragStartY = 0;
  let thumbDragStartScroll = 0;
  let thumbMoved = false;

  // Desktop thumbnail rail: use the small 600 AVIF rendition (no LQIP, no
  // full-resolution fallback) so thumbnails stay light. Products without AVIF
  // renditions keep their raster image.
  const thumbSrc = (slide: any) => {
    const src = slide?.image || slide?.src || slide?.imageBig || "";
    return hasRenditions(src)
      ? renditionUrl(src, 600, { ratio: 1, format: "avif" }) || src
      : src;
  };

  // Thumbnail failed to load. Two cases share this handler:
  //   1. a photo whose 600 rendition is missing -> retry the original image;
  //   2. a video whose CDN poster cannot be shown -> the base URL would fail
  //      too, so step to the JPEG still (`posterFrame`) and, if that fails as
  //      well, to the product's own photo (`posterFallback`).
  // A source is never assigned twice, so a failing fallback cannot loop. The
  // attribute, not `img.src`, is compared: `src` is resolved to an absolute
  // URL and never equals a relative one.
  const onThumbError = (e: Event, slide: any) => {
    const img = e.target as HTMLImageElement | null;
    if (!img) return;

    const current = img.getAttribute("src") || "";
    const source = img.closest("picture")?.querySelector("source");
    // The <source> rendition failed again after a fallback was assigned: the
    // thumbnail scrolled back into view and Vue put its srcset back. Drop the
    // srcset so the working fallback shows again, without moving the chain on.
    if (
      source?.getAttribute("srcset") &&
      current &&
      !current.startsWith("data:") &&
      img.currentSrc &&
      img.currentSrc !== img.src
    ) {
      source.removeAttribute("srcset");
      return;
    }
    const full = slide?.image || slide?.imageBig || slide?.src || "";
    const isVideo = slide?.type === "video";
    const frame = slide?.posterFrame || "";
    const fallback = slide?.posterFallback || "";
    const next = !isVideo
      ? full
      : frame && current !== frame && current !== fallback
      ? frame
      : fallback;

    if (!next || current === next) return;

    source?.removeAttribute("srcset");
    img.src = next;
  };

  // Scroll only the thumbnail track (never the page) so the active thumb stays visible.
  const ensureThumbVisible = (index: number) => {
    const el = thumbsTrackRef.value;
    if (!el) return;
    const child = el.children[index] as HTMLElement | undefined;
    if (!child) return;
    const top = child.offsetTop;
    const bottom = top + child.offsetHeight;
    if (top < el.scrollTop) {
      el.scrollTo({ top, behavior: "smooth" });
    } else if (bottom > el.scrollTop + el.clientHeight) {
      el.scrollTo({ top: bottom - el.clientHeight, behavior: "smooth" });
    }
  };

  // Thumbnail click -> drive the carousel (ignored if it was a drag-scroll).
  const goToGallerySlide = (index: number) => {
    if (thumbMoved) {
      thumbMoved = false;
      return;
    }
    desktopGalleryRef.value?.goTo?.(index);
  };

  // Carousel settled/changed -> highlight the matching thumbnail and keep it in view.
  const onGallerySlideChange = ({ index }: { index: number }) => {
    desktopGalleryIndex.value = index;
    ensureThumbVisible(index);
  };

  // Drag the thumbnail strip to scroll it (same feel as dragging the photo).
  const onThumbDragMove = (e: MouseEvent) => {
    const el = thumbsTrackRef.value;
    if (!el) return;
    const dy = e.clientY - thumbDragStartY;
    if (Math.abs(dy) > 4) thumbMoved = true; // distinguishes a drag from a click
    el.scrollTop = thumbDragStartScroll - dy;
  };

  const onThumbDragEnd = () => {
    thumbGrabbing.value = false;
    window.removeEventListener("mousemove", onThumbDragMove);
    window.removeEventListener("mouseup", onThumbDragEnd);
  };

  const onThumbDragStart = (e: MouseEvent) => {
    const el = thumbsTrackRef.value;
    if (!el) return;
    thumbGrabbing.value = true;
    thumbMoved = false;
    thumbDragStartY = e.clientY;
    thumbDragStartScroll = el.scrollTop;
    window.addEventListener("mousemove", onThumbDragMove);
    window.addEventListener("mouseup", onThumbDragEnd);
  };

  // Mouse wheel over the thumbnail strip scrolls the strip (not the page), as
  // long as it can still move; at the ends the page scrolls normally.
  const onThumbWheel = (e: WheelEvent) => {
    const el = thumbsTrackRef.value;
    if (!el || el.scrollHeight <= el.clientHeight) return;
    const atTop = el.scrollTop <= 0;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    if ((e.deltaY < 0 && !atTop) || (e.deltaY > 0 && !atBottom)) {
      e.preventDefault();
      el.scrollTop += e.deltaY;
    }
  };

  onBeforeUnmount(() => {
    thumbnailObserver?.disconnect();
    onThumbDragEnd();
  });

  return {
    mediaItems,
    // Desktop vertical-thumbnail gallery
    desktopGalleryRef,
    desktopGalleryIndex,
    thumbsTrackRef,
    thumbGrabbing,
    visibleThumbIndices,
    loadedThumbnailSources,
    onThumbLoad,
    thumbSrc,
    onThumbError,
    goToGallerySlide,
    onGallerySlideChange,
    onThumbDragStart,
    onThumbWheel,
  };
}

export const productThumbnailOptions = {
  fixedWidth: 95,
  fixedHeight: 95,
  isNavigation: true,
  gap: 20,
  arrows: true,
  pagination: false,
  wheel: true,
  releaseWheel: true,
};
